import type { Route } from "./+types/newsroom";
import { MarketingLayout } from "~/components/marketing/MarketingLayout";
import { detectLocale, translations } from "~/i18n/index";
import { useT } from "~/i18n/context";
import { socialMeta } from "~/lib/seo";
import {
  listPublishedNewsroomArticles,
  getFeaturedNewsroomArticles,
  listNewsroomArticlesByCategory,
} from "@caratzon/server";
import { Link, useLoaderData, useSearchParams } from "react-router";
import type { NewsroomArticle, NewsroomCategory } from "@caratzon/domain";
import { Clock, ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { resolveRenderUrl } from "~/lib/utils";

export async function loader({ request }: Route.LoaderArgs) {
  const locale = detectLocale(
    request.headers.get("Cookie"),
    request.headers.get("Accept-Language"),
  );
  const url = new URL(request.url);
  const VALID_CATEGORIES: NewsroomCategory[] = ["company", "product", "partnership", "industry", "press_release"];
  const rawCategory = url.searchParams.get("category");
  const category = rawCategory && VALID_CATEGORIES.includes(rawCategory as NewsroomCategory)
    ? (rawCategory as NewsroomCategory)
    : null;
  const page = Math.max(1, Number(url.searchParams.get("page") ?? "1"));

  const [articles, featured] = await Promise.all([
    category
      ? listNewsroomArticlesByCategory(category, { page, pageSize: 12 })
      : listPublishedNewsroomArticles({ page, pageSize: 12 }),
    category ? Promise.resolve([]) : getFeaturedNewsroomArticles(),
  ]);

  return { locale, articles, featured, category };
}

export function meta({ data }: Route.MetaArgs) {
  const locale = data?.locale ?? "en";
  const t = translations[locale]?.newsroom?.meta;
  const title = t?.title ?? "Newsroom — Caratzon";
  const description =
    t?.description ??
    "Latest news, press releases, and updates from Caratzon. Company announcements, product launches, partnerships, and industry insights.";
  return [
    { title },
    { name: "description", content: description },
    ...socialMeta({ title, description, pathname: "/newsroom", locale }),
    {
      tagName: "link",
      rel: "alternate",
      type: "application/rss+xml",
      title: "Caratzon Newsroom RSS",
      href: "/newsroom/rss.xml",
    },
  ];
}

export default function NewsroomIndexPage() {
  const { articles, featured, category } = useLoaderData<typeof loader>();
  const { t } = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const n = t?.newsroom;

  const categories = [
    { value: null, label: n?.categories?.all ?? "All" },
    { value: "company", label: n?.categories?.company ?? "Company" },
    { value: "product", label: n?.categories?.product ?? "Product" },
    { value: "partnership", label: n?.categories?.partnership ?? "Partnership" },
    { value: "industry", label: n?.categories?.industry ?? "Industry" },
    { value: "press_release", label: n?.categories?.press_release ?? "Press Release" },
  ] as const;

  return (
    <MarketingLayout>
      {/* ── Hero ── */}
      <section className="mx-auto max-w-6xl px-6 py-16 md:py-24">
        <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground">
          {n?.hero?.issueLabel ?? "Newsroom"}
        </p>
        <div className="mt-3 w-8 border-t border-foreground/20" />
        <h1 className="mt-4 font-display text-5xl md:text-6xl font-light italic tracking-tight leading-[1.1]">
          {n?.hero?.title ?? "Latest from Caratzon"}
        </h1>
        <p className="mt-4 text-base text-muted-foreground max-w-md">
          {n?.hero?.subtitle ??
            "Company news, press releases, product updates, and industry insights."}
        </p>
        <div className="mt-10 border-t border-foreground/10" />
      </section>

      {/* ── Featured ── */}
      {!category && featured.length > 0 && (
        <section className="mx-auto max-w-6xl px-6 pb-12">
          <p className="text-[10px] uppercase tracking-[0.28em] text-muted-foreground mb-6">
            {n?.featured ?? "Featured"}
          </p>

          {/* Primary featured — dominant hero treatment */}
          <Link
            to={`/newsroom/${featured[0].slug}`}
            className="group block border-t border-b border-foreground/10 py-8 mb-10"
          >
            {featured[0].ogImage && (
              <div className="mb-6 overflow-hidden bg-muted" style={{ aspectRatio: "16/9" }}>
                <img
                  src={resolveRenderUrl(featured[0].ogImage)}
                  alt={featured[0].title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  loading="lazy"
                />
              </div>
            )}
            <span className="text-[10px] uppercase tracking-[0.24em] text-primary">
              {n?.categories?.[featured[0].category as keyof typeof n.categories] ??
                featured[0].category}
            </span>
            <h2 className="mt-3 font-display text-3xl md:text-4xl font-medium tracking-tight leading-tight line-clamp-3 group-hover:text-teal transition-colors">
              {featured[0].title}
            </h2>
            <p className="mt-3 text-muted-foreground line-clamp-2 max-w-2xl">
              {featured[0].excerpt}
            </p>
            <div className="mt-6 flex items-center justify-between">
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" aria-hidden="true" />
                {featured[0].readingTimeMinutes} {n?.readingTime ?? "min read"}
                <span className="mx-1.5">·</span>
                {featured[0].publishedAt
                  ? new Date(featured[0].publishedAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  : ""}
              </span>
              <ArrowRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </div>
          </Link>

          {/* Secondary featured — 2-column */}
          {featured.length > 1 && (
            <div className="grid gap-8 md:grid-cols-2 mb-12">
              {featured.slice(1, 3).map((article) => (
                <FeaturedSecondaryCard key={article.id} article={article} />
              ))}
            </div>
          )}
        </section>
      )}

      {/* ── Article list ── */}
      <section className="mx-auto max-w-6xl px-6 pb-20">
        {/* Category nav */}
        <div className="flex items-center flex-wrap gap-y-2 mb-10">
          {categories.map((cat, i) => (
            <span key={cat.value ?? "all"} className="flex items-center">
              {i > 0 && (
                <span className="select-none text-muted-foreground/30 mx-3">·</span>
              )}
              <button
                aria-pressed={category === cat.value}
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  if (cat.value) {
                    params.set("category", cat.value);
                  } else {
                    params.delete("category");
                  }
                  params.delete("page");
                  setSearchParams(params);
                }}
                className={`text-xs uppercase tracking-[0.2em] transition-colors ${
                  category === cat.value
                    ? "text-foreground border-b border-foreground pb-0.5"
                    : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {cat.label}
              </button>
            </span>
          ))}
        </div>

        {/* Article grid — flat editorial cards with top rules */}
        {articles.items.length === 0 ? (
          <p className="border-t border-foreground/10 py-16 text-center text-muted-foreground">
            {n?.noArticles ?? "No articles yet. Check back soon!"}
          </p>
        ) : (
          <div className="grid gap-x-10 sm:grid-cols-2 lg:grid-cols-3">
            {articles.items.map((article) => (
              <ArticleCard key={article.id} article={article} />
            ))}
          </div>
        )}

        {/* Pagination — prev / next */}
        {articles.pagination.totalPages > 1 && (
          <div className="mt-16 flex items-center justify-center gap-10 border-t border-foreground/10 pt-8">
            {articles.pagination.page > 1 ? (
              <button
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set("page", String(articles.pagination.page - 1));
                  setSearchParams(params);
                }}
                className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Previous
              </button>
            ) : (
              <span className="w-24" />
            )}
            <span className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
              Page {articles.pagination.page} of {articles.pagination.totalPages}
            </span>
            {articles.pagination.page < articles.pagination.totalPages ? (
              <button
                onClick={() => {
                  const params = new URLSearchParams(searchParams);
                  params.set("page", String(articles.pagination.page + 1));
                  setSearchParams(params);
                }}
                className="flex items-center gap-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
              >
                Next <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </button>
            ) : (
              <span className="w-24" />
            )}
          </div>
        )}
      </section>
    </MarketingLayout>
  );
}

function FeaturedSecondaryCard({ article }: { article: NewsroomArticle }) {
  const { t } = useT();
  const n = t?.newsroom;
  return (
    <Link
      to={`/newsroom/${article.slug}`}
      className="group block border-t border-foreground/10 py-6"
    >
      {article.ogImage && (
        <div className="mb-4 overflow-hidden bg-muted" style={{ aspectRatio: "16/9" }}>
          <img
            src={resolveRenderUrl(article.ogImage)}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            loading="lazy"
          />
        </div>
      )}
      <span className="text-[10px] uppercase tracking-[0.24em] text-primary">
        {n?.categories?.[article.category as keyof typeof n.categories] ?? article.category}
      </span>
      <h3 className="mt-2 font-display text-2xl font-medium tracking-tight leading-snug line-clamp-3 group-hover:text-teal transition-colors">
        {article.title}
      </h3>
      <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{article.excerpt}</p>
      <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Clock className="h-3 w-3" aria-hidden="true" />
        {article.readingTimeMinutes} {n?.readingTime ?? "min read"}
        <span className="mx-1.5">·</span>
        {article.publishedAt
          ? new Date(article.publishedAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : ""}
      </div>
    </Link>
  );
}

function ArticleCard({ article }: { article: NewsroomArticle }) {
  const { t } = useT();
  const n = t?.newsroom;
  return (
    <Link
      to={`/newsroom/${article.slug}`}
      className="group block border-t border-foreground/10 py-6"
    >
      {article.ogImage && (
        <div className="mb-3 overflow-hidden bg-muted" style={{ aspectRatio: "16/9" }}>
          <img
            src={resolveRenderUrl(article.ogImage)}
            alt={article.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
            loading="lazy"
          />
        </div>
      )}
      <span className="text-[10px] uppercase tracking-[0.24em] text-primary">
        {n?.categories?.[article.category as keyof typeof n.categories] ?? article.category}
      </span>
      <h3 className="mt-2 font-display text-xl font-medium tracking-tight leading-snug line-clamp-2 group-hover:text-teal transition-colors">
        {article.title}
      </h3>
      <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{article.excerpt}</p>
      <div className="mt-4 flex items-center gap-1.5 text-xs text-muted-foreground">
        <Clock className="h-3 w-3" aria-hidden="true" />
        {article.readingTimeMinutes} {n?.readingTime ?? "min read"}
        <span className="mx-1.5">·</span>
        {article.publishedAt
          ? new Date(article.publishedAt).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })
          : ""}
      </div>
    </Link>
  );
}
